import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Title, Button, Table, TableHeaderRow, TableHeaderCell, TableRow, TableCell, Dialog, Bar, Input, Label, CheckBox, MessageStrip, FlexBox, BusyIndicator, Switch, Text, ObjectStatus } from '@ui5/webcomponents-react';
import type { AppRoleRow } from '@b1links/core';
import { api } from '../../api/client';
import { PageHeader } from '../../components/Responsive';

const parsePerms = (csv?: string) => new Set((csv ?? '').split(',').map(s => s.trim()).filter(Boolean));

/** Role editor: toggles View/Edit per screen-entity + admin + full access. Stored in SAP UDT @APPROLE (U_Permissions csv). */
export function RolesAdmin() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const roles = useQuery({ queryKey: ['admin-roles'], queryFn: api.adminRoles });
  const cat = useQuery({ queryKey: ['perm-catalog'], queryFn: api.permissionCatalog });
  const [edit, setEdit] = useState<AppRoleRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState(''); const [name, setName] = useState('');
  const [perms, setPerms] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null); const [busy, setBusy] = useState(false);

  const open = (r?: AppRoleRow) => { setErr(null); setCreating(!r); setEdit(r ?? { Code: '', Name: '' }); setCode(r?.Code ?? ''); setName(r?.Name ?? ''); setPerms(parsePerms(r?.U_Permissions)); };
  const close = () => setEdit(null);
  const full = perms.has('*');
  const toggle = (p: string, on: boolean) => setPerms(s => { const n = new Set(s); on ? n.add(p) : n.delete(p); return n; });

  const save = async () => {
    if (!code || !name) { setErr('Code and name are required'); return; }
    setBusy(true); setErr(null);
    try {
      const U_Permissions = [...perms].join(',');
      if (creating) await api.createRole({ Code: code, Name: name, U_Permissions });
      else await api.updateRole(code, { Name: name, U_Permissions });
      await qc.invalidateQueries({ queryKey: ['admin-roles'] }); close();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  const del = async (r: AppRoleRow) => { if (!window.confirm(`Delete role ${r.Code}?`)) return; try { await api.deleteRole(r.Code); qc.invalidateQueries({ queryKey: ['admin-roles'] }); } catch (e) { alert((e as Error).message); } };

  if (roles.isLoading || cat.isLoading) return <FlexBox justifyContent="Center" style={{ padding: '3rem' }}><BusyIndicator active /></FlexBox>;

  return (
    <div>
      <PageHeader title="Roles" style={{ marginBottom: '1rem' }} actions={<Button design="Emphasized" icon="add" onClick={() => open()}>New role</Button>} />
      <Table overflowMode="Popin" headerRow={<TableHeaderRow><TableHeaderCell minWidth="6rem" importance={2}><span>Code</span></TableHeaderCell><TableHeaderCell minWidth="9rem"><span>Name</span></TableHeaderCell><TableHeaderCell minWidth="8rem"><span>Access</span></TableHeaderCell><TableHeaderCell minWidth="6rem" importance={1}><span></span></TableHeaderCell></TableHeaderRow>}>
        {(roles.data ?? []).map(r => { const p = parsePerms(r.U_Permissions); return (
          <TableRow key={r.Code}>
            <TableCell><span>{r.Code}</span></TableCell><TableCell><span>{r.Name}</span></TableCell>
            <TableCell>{p.has('*') ? <ObjectStatus state="Information">Full access</ObjectStatus> : <Text>{p.size} permission(s)</Text>}</TableCell>
            <TableCell><FlexBox style={{ gap: '0.25rem' }}><Button icon="edit" design="Transparent" tooltip="Edit" onClick={() => open(r)} /><Button icon="locked" design="Transparent" tooltip="Screen access" onClick={() => nav(`/admin/access?kind=role&code=${encodeURIComponent(r.Code)}`)} /><Button icon="delete" tooltip="Delete" design="Transparent" onClick={() => del(r)} /></FlexBox></TableCell>
          </TableRow>); })}
      </Table>

      {edit && (
        <Dialog open headerText={creating ? 'New role' : `Edit role ${edit.Code}`} style={{ width: 'min(640px, 96vw)' }}
          footer={<Bar endContent={<><Button onClick={close}>Cancel</Button><Button design="Emphasized" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</Button></>} />}>
          {err && <MessageStrip design="Negative" onClose={() => setErr(null)}>{err}</MessageStrip>}
          <FlexBox direction="Column" style={{ gap: '0.75rem', padding: '0.5rem 0' }}>
            <FlexBox wrap="Wrap" style={{ gap: '1rem' }}>
              <div><Label>Code</Label><Input value={code} disabled={!creating} onInput={e => setCode(e.target.value ?? '')} /></div>
              <div style={{ flex: 1, minWidth: '10rem' }}><Label>Name</Label><Input value={name} style={{ width: '100%' }} onInput={e => setName(e.target.value ?? '')} /></div>
            </FlexBox>
            <FlexBox alignItems="Center" style={{ gap: '0.5rem' }}><Switch checked={full} onChange={e => setPerms(e.target.checked ? new Set(['*']) : new Set())} /><Label>Full access to everything (super role)</Label></FlexBox>
            {!full && <>
              <Title level="H5" style={{ marginTop: '0.5rem' }}>Administration</Title>
              {cat.data!.fixed.map(f => <FlexBox key={f.id} alignItems="Center" style={{ gap: '0.5rem' }}><CheckBox checked={perms.has(f.id)} onChange={e => toggle(f.id, e.target.checked)} /><Text>{f.label}</Text></FlexBox>)}
              <Title level="H5" style={{ marginTop: '0.5rem' }}>Screens</Title>
              <Table overflowMode="Popin" headerRow={<TableHeaderRow><TableHeaderCell minWidth="8rem" importance={2}><span>Entity</span></TableHeaderCell><TableHeaderCell width="6rem" minWidth="4rem" importance={1}><span>View</span></TableHeaderCell><TableHeaderCell width="6rem" minWidth="4rem" importance={1}><span>Edit</span></TableHeaderCell></TableHeaderRow>}>
                {cat.data!.entities.map(en => (
                  <TableRow key={en.entity}>
                    <TableCell><span>{en.label} <Text style={{ color: 'var(--sapNeutralTextColor)' }}>({en.entity})</Text></span></TableCell>
                    <TableCell><CheckBox checked={perms.has(en.view)} onChange={e => toggle(en.view, e.target.checked)} /></TableCell>
                    <TableCell><CheckBox checked={perms.has(en.edit)} onChange={e => { toggle(en.edit, e.target.checked); if (e.target.checked) toggle(en.view, true); }} /></TableCell>
                  </TableRow>
                ))}
              </Table>
            </>}
          </FlexBox>
        </Dialog>
      )}
    </div>
  );
}
